/**
 * usePinLockout (Plan 01-12 Task 1)
 *
 * State machine that translates the server's `Retry-After: <seconds>` header
 * into a live second-by-second countdown plus the matching Bokmål copy.
 *
 * Per UI-SPEC §"Interaction Contracts §3 PIN keypad":
 *   - 5 wrong attempts → 30-second lockout → `t('pin.lockout', { time: '0:30' })`
 *   - 10 wrong attempts → 300-second lockout → `t('pin.lockout_long', { time: '5:00' })`
 *     (long form carries the "spør en voksen om å prøve igjen" suffix)
 *
 * The hook is display-only: it does not talk to the server.  The consumer
 * (LoginRoute) reads `res.headers.get('Retry-After')` and calls
 * `startLockout(n)`.  When the countdown reaches zero the hook resets
 * itself — the consumer does not need to manage cleanup.
 *
 * Timer discipline:
 *   - Exactly one `setInterval` is ever active for this hook instance.
 *   - `startLockout` cancels any in-flight countdown before starting a new one.
 *   - `useEffect` returns a cleanup closure so React unmount clears the timer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Sekunder → `m:ss`. Delt av PIN-tastaturet og passordinnloggingen (#141). */
export function formatCountdown(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const m = Math.floor(safe / 60);
  const s = safe - m * 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** 300 seconds = 5 minutes — the threshold for the "long" lockout copy. */
const LONG_LOCKOUT_THRESHOLD_SECONDS = 300;

export interface UsePinLockoutReturn {
  /** Current countdown value in seconds; 0 when not locked. */
  secondsRemaining: number;
  /** Translated Bokmål copy for the lockout banner; null when not locked. */
  lockoutMessage: string | null;
  /** Start the countdown from `retryAfterSeconds` seconds.  Cancels any prior countdown. */
  startLockout: (retryAfterSeconds: number) => void;
  /** Stop the countdown and clear the message immediately. */
  cancel: () => void;
}

export interface LockoutMessageKeys {
  short: string;
  long: string;
}

const PIN_LOCKOUT_KEYS: LockoutMessageKeys = { short: 'pin.lockout', long: 'pin.lockout_long' };

/** `keys` velger teksten; passordinnloggingen bruker sin egen (#141). */
export function usePinLockout(keys: LockoutMessageKeys = PIN_LOCKOUT_KEYS): UsePinLockoutReturn {
  const { t } = useTranslation();
  const [secondsRemaining, setSeconds] = useState<number>(0);
  const [isLong, setIsLong] = useState<boolean>(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cancel = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setSeconds(0);
    setIsLong(false);
  }, []);

  const startLockout = useCallback(
    (retryAfterSeconds: number) => {
      // Always cancel any in-flight countdown before starting fresh — this
      // guards against overlapping intervals from rapid-fire 401 responses.
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      const initial = Math.max(0, Math.floor(retryAfterSeconds));
      setSeconds(initial);
      setIsLong(initial >= LONG_LOCKOUT_THRESHOLD_SECONDS);
      if (initial === 0) return;
      intervalRef.current = setInterval(() => {
        setSeconds((prev) => {
          if (prev <= 1) {
            // Final tick — clean up the interval and reset the long-form flag.
            if (intervalRef.current !== null) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            setIsLong(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [],
  );

  // Guarantee cleanup on unmount so orphaned intervals never leak.
  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const lockoutMessage =
    secondsRemaining > 0
      ? t(isLong ? keys.long : keys.short, {
          time: formatCountdown(secondsRemaining),
        })
      : null;

  return { secondsRemaining, lockoutMessage, startLockout, cancel };
}
